import { useState, useEffect } from 'react';
import type { VerificationCode } from '../hooks/useGeolocation';

interface GpsVerificationReasonModalProps {
  isOpen: boolean;
  reasonCode: VerificationCode | null;
  distance: number | null;
  storeName: string;
  onCancel: () => void;
  onSubmit: (reasonText: string) => void;
}

function describeReason(code: VerificationCode | null, distance: number | null, storeName: string): string {
  switch (code) {
    case 'off_site':
      if (distance != null) {
        const display = distance < 1000 ? `${Math.round(distance)}m` : `${(distance / 1000).toFixed(1)}km`;
        return `You appear to be ${display} from ${storeName}.`;
      }
      return `You appear to be away from ${storeName}.`;
    case 'gps_low_accuracy':
      return 'Your GPS fix is too imprecise to confirm you are at the store.';
    case 'gps_unavailable':
      return 'We could not read your location, so we cannot confirm you are at the store.';
    case 'store_missing_coords':
      return `${storeName} has no coordinates on file, so we cannot verify your location.`;
    default:
      return 'We could not verify you are at the store.';
  }
}

export default function GpsVerificationReasonModal({
  isOpen,
  reasonCode,
  distance,
  storeName,
  onCancel,
  onSubmit,
}: GpsVerificationReasonModalProps) {
  const [reasonText, setReasonText] = useState('');

  // Clear text whenever the modal closes/opens with a new context.
  useEffect(() => {
    if (!isOpen) setReasonText('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const trimmed = reasonText.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Reason Required</h3>
            <p className="text-sm text-slate-500">{describeReason(reasonCode, distance, storeName)}</p>
          </div>
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Please provide a reason for this manual entry
          </label>
          <textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="e.g., entered later from home, no signal at store, store hours conflicted with route..."
            className="w-full rounded-lg border border-slate-200 p-3 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            rows={3}
            autoFocus
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!reasonText.trim()}
            className="flex-1 rounded-lg bg-amber-600 py-2.5 font-medium text-white transition-colors hover:bg-amber-700 disabled:bg-slate-300"
          >
            Save Entry
          </button>
        </div>
      </div>
    </div>
  );
}
